import { useState, useEffect } from "react";
import { FaAngleUp, FaAngleDown, FaUser, FaEnvelope } from "react-icons/fa";
import { BsTelephoneFill } from "react-icons/bs";
import { toast } from "react-toastify";
import Cookies from "universal-cookie";

export const DeliveryOrderTrucker = ({
    tid,
    truckerInfo = null,
    isNew = true,
    userId,
    addTrucker,
    closeModal,
}) => {
    const isEditMode = !isNew && Boolean(truckerInfo);
    const [opened, setOpened] = useState(true);
    const [name, setName] = useState(truckerInfo?.name || "");
    const [phone, setPhone] = useState(truckerInfo?.phone || "");
    const [displayName, setDisplayName] = useState(
        isEditMode ? `Edit ${truckerInfo?.name || "Trucker"}` : "New Trucker"
    );
    const [mainEmail, setMainEmail] = useState(
        truckerInfo?.trucker_emails?.[0]?.email || truckerInfo?.email || ""
    );
    const [ccEmails, setCcEmails] = useState(
        truckerInfo?.trucker_emails?.[0]?.cc_emails && truckerInfo.trucker_emails[0].cc_emails.length > 0
            ? truckerInfo.trucker_emails[0].cc_emails
            : [""]
    );
    const [numberOfEmailsError, setNumberOfEmailsError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [existingTruckers, setExistingTruckers] = useState([]);
    const [fetching, setFetching] = useState(true);
    const cookies = new Cookies();
    const token = cookies.get("token_user");
    const URL = process.env.REACT_APP_API_URL + "/user/settings";

    const openTab = () => {
        setOpened(!opened);
    };

    const addEmailInput = (e) => {
        e.preventDefault();
        if (ccEmails.length < 10) {
            setCcEmails([...ccEmails, ""]);
        } else {
            setNumberOfEmailsError(true);
        }
    };

    const handleUpdateEmail = (i, value) => {
        const updatedEmails = [...ccEmails];
        updatedEmails[i] = value;
        setCcEmails(updatedEmails);
    };

    const handleDeleteEmail = (i) => {
        const updatedEmails = [...ccEmails];
        updatedEmails.splice(i, 1);
        setCcEmails(updatedEmails);
        if (updatedEmails.length < 10) {
            setNumberOfEmailsError(false);
        }
    };

    const handleNameChange = (e) => {
        setName(e.target.value);
    };

    const handlePhoneChange = (e) => {
        setPhone(e.target.value);
    };

    const handleMainEmailChange = (e) => {
        setMainEmail(e.target.value);
    };

    const fetchUserSettings = async () => {
        setFetching(true);
        try {
            const response = await fetch(`${URL}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (response.status === 200) {
                setExistingTruckers(data.user?.truckers || []);
            } else {
                setError("Failed to fetch user settings.");
            }
        } catch (e) {
            setError("An error occurred while fetching user settings.");
            console.error(e);
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        fetchUserSettings();
    }, []);

    useEffect(() => {
        if (truckerInfo) {
            setName(truckerInfo?.name || "");
            setPhone(truckerInfo?.phone || "");
            setDisplayName(isEditMode ? `Edit ${truckerInfo?.name || "Trucker"}` : "New Trucker");
            setMainEmail(truckerInfo?.trucker_emails?.[0]?.email || truckerInfo?.email || "");
            setCcEmails(
                truckerInfo?.trucker_emails?.[0]?.cc_emails && truckerInfo.trucker_emails[0].cc_emails.length > 0
                    ? truckerInfo.trucker_emails[0].cc_emails
                    : [""]
            );
        }
    }, [truckerInfo, isEditMode]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) {
            toast.error("Trucker name is required");
            return;
        }

        setLoading(true);

        const truckerPayload = {
            ...(truckerInfo?.id ? { id: truckerInfo.id } : {}),
            name: name.trim(),
            phone: phone.trim(),
            email: mainEmail.trim(),
            cc_emails: ccEmails.filter((email) => email && email.trim()),
        };

        let updatedList = [];
        const targetId = truckerInfo?.id;
        const matchIndex = existingTruckers.findIndex(
            (t) => (targetId ? t.id === targetId : t.name.trim().toLowerCase() === (truckerInfo?.name || "").trim().toLowerCase())
        );

        if (isEditMode && matchIndex !== -1) {
            updatedList = existingTruckers.map((t, idx) => {
                if (idx === matchIndex) {
                    return {
                        id: t.id,
                        name: truckerPayload.name,
                        phone: truckerPayload.phone,
                        email: truckerPayload.email,
                        cc_emails: truckerPayload.cc_emails,
                    };
                }
                return {
                    id: t.id,
                    name: t.name,
                    phone: t.phone || "",
                    email: t.trucker_emails?.[0]?.email || "",
                    cc_emails: t.trucker_emails?.[0]?.cc_emails || [],
                };
            });
        } else {
            updatedList = [
                ...existingTruckers.map((t) => ({
                    id: t.id,
                    name: t.name,
                    phone: t.phone || "",
                    email: t.trucker_emails?.[0]?.email || "",
                    cc_emails: t.trucker_emails?.[0]?.cc_emails || [],
                })),
                truckerPayload,
            ];
        }

        const truckersData = {
            truckers: updatedList,
        };

        try {
            const response = await fetch(`${URL}/save`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(truckersData),
            });

            if (response.ok) {
                const result = await response.json();
                const successMsg = isEditMode ? "Trucker updated successfully" : "Trucker created successfully";
                toast.success(successMsg);

                let savedTrucker = null;
                if (result.truckers && Array.isArray(result.truckers)) {
                    if (targetId) {
                        savedTrucker = result.truckers.find((t) => t.id === targetId);
                    }
                    if (!savedTrucker) {
                        savedTrucker = result.truckers.find(
                            (t) => t.name.toLowerCase() === truckerPayload.name.toLowerCase()
                        );
                    }
                    if (!savedTrucker) {
                        savedTrucker = result.truckers[result.truckers.length - 1];
                    }
                }

                if (savedTrucker && addTrucker) {
                    addTrucker(savedTrucker);
                }
                setDisplayName(name);
                if (closeModal) closeModal();
                if (typeof window.getShipmentData === "function") {
                    window.getShipmentData();
                }
            } else {
                toast.error(isEditMode ? "Failed to update trucker" : "Failed to create trucker");
            }
        } catch (error) {
            toast.error("An error occurred while saving the trucker");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="individual-header" onClick={openTab} style={{ pointerEvents: fetching ? 'none' : 'auto', opacity: fetching ? 0.5 : 1 }}>
                <h3 className="name">{displayName}</h3>
                <div className="arrow">{opened ? <FaAngleUp /> : <FaAngleDown />}</div>
            </div>
            {opened && (
                <div className="trucker-form" style={{ padding: "20px", maxWidth: "600px", backgroundColor: "#f4f4f4", borderRadius: "5px", pointerEvents: fetching ? 'none' : 'auto', opacity: fetching ? 0.5 : 1 }}>
                    {fetching ? (
                        <div style={{ textAlign: 'center' }}>Loading...</div>
                    ) : (
                        <>
                            <div className="input-section name" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                <div className="input-container name">
                                    <label className="input-label" style={{ fontSize: "14px", marginBottom: "5px" }}>Name</label>
                                    <div className="input" style={{ position: "relative" }}>
                                        <FaUser style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "grey" }} />
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={handleNameChange}
                                            style={{ paddingLeft: "35px", flex: "1", padding: "10px 10px 10px 35px", backgroundColor: "white", borderRadius: "5px", border: "1px solid #d3d3d3" }}
                                        />
                                    </div>
                                </div>
                                <div className="input-container phone">
                                    <label className="input-label" style={{ fontSize: "14px", marginBottom: "5px" }}>Phone Number</label>
                                    <div className="input" style={{ position: "relative" }}>
                                        <BsTelephoneFill style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "grey" }} />
                                        <input
                                            type="text"
                                            value={phone}
                                            onChange={handlePhoneChange}
                                            placeholder="Phone Number"
                                            style={{ paddingLeft: "35px", flex: "1", padding: "10px 10px 10px 35px", backgroundColor: "white", borderRadius: "5px", border: "1px solid #d3d3d3" }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="input-section render-emails" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                <div className="input-container main-email">
                                    <label className="input-label" style={{ fontSize: "14px", marginBottom: "5px" }}>Main Email Address</label>
                                    <div className="bottom" style={{ position: "relative" }}>
                                        <FaEnvelope style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "grey" }} />
                                        <input
                                            type="text"
                                            value={mainEmail}
                                            onChange={handleMainEmailChange}
                                            style={{ paddingLeft: "35px", flex: "1", padding: "10px 10px 10px 35px", backgroundColor: "white", borderRadius: "5px", border: "1px solid #d3d3d3" }}
                                        />
                                    </div>
                                </div>
                                <div className="input-container cc-emails">
                                    <label className="input-label" style={{ fontSize: "14px", marginBottom: "5px" }}>CC Email Addresses</label>
                                    {ccEmails.map((email, i) => (
                                        <div className="existing-email" key={i}>
                                            <div className="bottom" style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "5px" }}>
                                                <div className="input" style={{ position: "relative", flex: "1" }}>
                                                    <FaEnvelope style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "grey" }} />
                                                    <input
                                                        type="text"
                                                        value={email}
                                                        onChange={(e) => handleUpdateEmail(i, e.target.value)}
                                                        style={{ paddingLeft: "35px", flex: "1", padding: "10px 10px 10px 35px", backgroundColor: "white", borderRadius: "5px", border: "1px solid #d3d3d3" }}
                                                    />
                                                </div>
                                                <button
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        height: "35px",
                                                        width: "35px",
                                                        borderRadius: "5px",
                                                        border: "1px solid #707070",
                                                        backgroundColor: "white",
                                                        cursor: "pointer",
                                                    }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleDeleteEmail(i);
                                                    }}
                                                >
                                                    <p style={{ margin: "0" }}>—</p>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="input-section add-email" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                {numberOfEmailsError && (
                                    <p
                                        style={{
                                            color: "var(--red)",
                                            marginBottom: "16px",
                                            textDecoration: "underline",
                                        }}
                                    >
                                        Maximum 10 emails per trucker.
                                    </p>
                                )}
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "8px" }}>
                                    <button
                                        style={{
                                            backgroundColor: "var(--green)",
                                            width: "46%",
                                            border: "none",
                                            color: "white",
                                            borderRadius: "5px",
                                            padding: "5px 10px",
                                            height: "fit-content",
                                        }}
                                        onClick={addEmailInput}
                                    >
                                        Add New Email
                                    </button>
                                    <button
                                        style={{
                                            backgroundColor: "var(--green)",
                                            width: "40%",
                                            border: "none",
                                            color: "white",
                                            borderRadius: "5px",
                                            padding: "5px 10px",
                                            height: "fit-content",
                                            marginLeft: "auto"
                                        }}
                                        onClick={handleSubmit}
                                        disabled={loading}
                                    >
                                        {loading ? <span>Loading...</span> : <p style={{ margin: "0" }}>Save</p>}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </>
    );
};

export default DeliveryOrderTrucker;
